package com.iregained.site.components.sections.header

import androidx.compose.runtime.*
import com.iregained.site.region.activeStrings
import com.iregained.site.theme.toSitePalette
import com.iregained.site.theme.unextractable
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.ObfuscatedEmailText
import com.iregained.site.utils.Res
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span

// UNIQUE ANIMATIONS: Curtain Drop for the main header, Subtle Dropdown Fade for menus
val HeaderCurtainDropKeyframes = Keyframes {
    0.percent { Modifier.translateY((-100).percent).opacity(0) }
    100.percent { Modifier.translateY(0.percent).opacity(1) }
}

val DropdownFadeKeyframes = Keyframes {
    0.percent { Modifier.translateY((-10).px).opacity(0) }
    100.percent { Modifier.translateY(0.px).opacity(1) }
}

@Composable
fun NavHeader(data: NavHeaderData = defaultNavHeaderData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.XL
    val palette = ColorMode.current.toSitePalette()
    var isMobileMenuOpen by remember { mutableStateOf(false) }

    val ctx = rememberPageContext()
    val currentPath = ctx.route.path.removeSuffix("/").ifEmpty { "/" }

    Column(
        modifier = Modifier.fillMaxWidth().backgroundColor(Colors.White)
            .boxShadow(0.px, 4.px, 20.px, color = palette.custom.darkText.toRgb().copyf(alpha = 0.1f))
            .position(Position.Sticky).top(0.px).zIndex(999)
            // Animated Header Drop
            .animation(
                HeaderCurtainDropKeyframes.toAnimation(
                    duration = 0.8.s, timingFunction = AnimationTimingFunction.EaseOut
                )
            )
    ) {
        if (isDesktop) {
            TopUtilityBar(data)
        }

        Row(
            modifier = Modifier.fillMaxWidth().padding(leftRight = 5.percent, topBottom = 15.px),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Link(path = "/") {
                Image(
                    src = data.logoPath, modifier = Modifier.unextractable().height(40.px).cursor(Cursor.Pointer)
                )
            }

            if (isDesktop) {
                Row(modifier = Modifier.gap(10.px), verticalAlignment = Alignment.CenterVertically) {
                    data.menuItems.forEach { item ->
                        key(item.title) {
                            if (item.subItems.isNotEmpty()) {
                                val isDropdownActive =
                                    item.subItems.any { it.path.removeSuffix("/").ifEmpty { "/" } == currentPath }
                                HeaderDropdown(
                                    title = item.title,
                                    items = item.subItems,
                                    isActive = isDropdownActive,
                                    currentPath = currentPath
                                )
                            } else if (item.path != null) {
                                val isLinkActive = item.path.removeSuffix("/").ifEmpty { "/" } == currentPath
                                HeaderLink(text = item.title, path = item.path, isActive = isLinkActive)
                            }
                        }
                    }
                }

                Link(path = data.ctaPath, modifier = Modifier.textDecorationLine(TextDecorationLine.None)) {
                    Box(
                        modifier = HeaderButtonStyle.toModifier().color(Colors.White)
                            .padding(leftRight = 24.px, topBottom = 12.px).borderRadius(6.px).cursor(Cursor.Pointer),
                        contentAlignment = Alignment.Center
                    ) {
                        SpanText(
                            data.ctaText,
                            modifier = Modifier.fontSize(15.px).fontWeight(FontWeight.Bold)
                                .whiteSpace(WhiteSpace.NoWrap)
                        )
                    }
                }
            } else {
                Box(
                    modifier = HamburgerIconStyle.toModifier().size(30.px).cursor(Cursor.Pointer)
                        .rotate(if (isMobileMenuOpen) 180.deg else 0.deg) // NEW: Rotates based on state
                        .transition(Transition.of("transform", 0.3.s)) // NEW: Smooths the rotation
                        .onClick { isMobileMenuOpen = !isMobileMenuOpen }, contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        text = if (isMobileMenuOpen) "✕" else "☰",
                        modifier = Modifier.fontSize(24.px).color(palette.brand.primary)
                    )
                }
            }
        }

        if (!isDesktop && isMobileMenuOpen) {
            Column(
                modifier = Modifier.fillMaxWidth().maxHeight(85.vh).overflow(Overflow.Auto)
                    .backgroundColor(Colors.White).padding(leftRight = 5.percent, topBottom = 20.px)
                    .borderTop(1.px, LineStyle.Solid, palette.custom.divider)
                    .borderBottom(3.px, LineStyle.Solid, palette.brand.primary)
            ) {
                data.menuItems.forEach { item ->
                    key(item.title) {
                        if (item.subItems.isNotEmpty()) {
                            val isDropdownActive =
                                item.subItems.any { it.path.removeSuffix("/").ifEmpty { "/" } == currentPath }
                            MobileHeaderDropdown(
                                title = item.title,
                                items = item.subItems,
                                isActive = isDropdownActive,
                                currentPath = currentPath,
                                onLinkClick = { isMobileMenuOpen = false })
                        } else if (item.path != null) {
                            val isLinkActive = item.path.removeSuffix("/").ifEmpty { "/" } == currentPath
                            MobileHeaderLink(
                                text = item.title,
                                path = item.path,
                                isActive = isLinkActive,
                                onClick = { isMobileMenuOpen = false })
                        }
                    }
                }

                Link(
                    path = data.ctaPath,
                    modifier = Modifier.textDecorationLine(TextDecorationLine.None).margin(leftRight = 16.px)
                        .onClick { isMobileMenuOpen = false }) {
                    Box(
                        modifier = HeaderButtonStyle.toModifier().color(Colors.White).fillMaxWidth().margin(top = 20.px)
                            .padding(topBottom = 12.px).borderRadius(6.px).cursor(Cursor.Pointer),
                        contentAlignment = Alignment.Center
                    ) {
                        SpanText(
                            data.ctaText,
                            modifier = Modifier.padding(leftRight = 20.px).fontSize(16.px).fontWeight(FontWeight.Bold)
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun TopUtilityBar(data: NavHeaderData) {
    val palette = ColorMode.current.toSitePalette()
    // Shared modifier to keep the text looking identical
    val utilityTextModifier =
        Modifier.fontSize(16.px).color(palette.custom.darkText).fontWeight(FontWeight.Medium).opacity(80.percent)
            .whiteSpace(WhiteSpace.NoWrap)

    Row(
        modifier = Modifier.fillMaxWidth().height(36.px).backgroundColor(palette.nearBackground)
            .padding(leftRight = 5.percent),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Right side: Phone and Email
        Row(modifier = Modifier.gap(20.px)) {
            // PHONE LINK (Uses standard SpanText)
            DynamicUtilityLink(
                iconPath = data.utilityPhoneIcon,
                actionPrefix = "tel:",
                rawValue = data.contactPhone.replace(Regex("[^0-9+]"), "")
            ) {
                SpanText(text = data.contactPhone, modifier = utilityTextModifier)
            }

            // EMAIL LINK (Uses the new stylized Composable)
            DynamicUtilityLink(
                iconPath = data.utilityEmailIcon, actionPrefix = "mailto:", rawValue = data.contactEmail
            ) {
                ObfuscatedEmailText(email = data.contactEmail, modifier = utilityTextModifier)
            }
        }
    }
}

@Composable
private fun DynamicUtilityLink(
    iconPath: String, actionPrefix: String, rawValue: String, content: @Composable () -> Unit
) {
    val palette = ColorMode.current.toSitePalette()
    // Remove href entirely
    A(
        attrs = Modifier.color(palette.custom.darkText).textDecorationLine(TextDecorationLine.None).cursor(Cursor.Pointer)
        .onClick { e ->
            e.preventDefault()
            kotlinx.browser.window.location.assign("$actionPrefix$rawValue")
        }.toAttrs { attr("role", "link"); attr("tabindex", "0") }) {
        Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(6.px)) {
            Image(iconPath, Modifier.unextractable().size(14.px))
            content() // Render whatever text component was passed in
        }
    }
}

@Composable
private fun HeaderDropdown(title: String, items: List<NavLinkData>, isActive: Boolean, currentPath: String) {
    var isHovered by remember { mutableStateOf(false) }
    val palette = ColorMode.current.toSitePalette()

    Box(modifier = Modifier.onMouseEnter { isHovered = true }.onMouseLeave { isHovered = false }
        .position(Position.Relative)) {
        Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.cursor(Cursor.Pointer).gap(4.px)) {
            Div(
                attrs = AnimatedHeaderLinkStyle.toModifier().fontSize(18.px)
                    .fontWeight(if (isActive) FontWeight.Bold else FontWeight.Medium).whiteSpace(WhiteSpace.NoWrap)
                    .then(
                        if (isActive) Modifier.backgroundColor(palette.brand.primarySoft).color(palette.brand.primary)
                        else Modifier
                    ).toAttrs {
                        ref { htmlElement ->
                            htmlElement.innerHTML = "$title ▾"
                            onDispose {}
                        }
                    })
        }
        if (isHovered) {
            Column(
                modifier = Modifier.position(Position.Absolute).top(100.percent).left(10.px).minWidth(200.px)
                    .backgroundColor(Colors.White).borderRadius(8.px)
                    .boxShadow(0.px, 10.px, 30.px, color = palette.custom.darkText.toRgb().copyf(alpha = 0.08f))
                    .overflow(Overflow.Hidden).padding(topBottom = 8.px).animation(
                        DropdownFadeKeyframes.toAnimation(
                            duration = 0.2.s, timingFunction = AnimationTimingFunction.EaseOut
                        )
                    )
            ) {
                items.forEach { linkData ->
                    key(linkData.text) {
                        val isSubActive = linkData.path.removeSuffix("/").ifEmpty { "/" } == currentPath
                        Link(
                            path = linkData.path,
                            // REMOVED: text = linkData.text
                            modifier = DropdownItemStyle.toModifier().fontSize(18.px)
                                .fontWeight(if (isSubActive) FontWeight.Bold else FontWeight.Normal)
                                .color(if (isSubActive) palette.brand.primary else palette.custom.darkText)
                                .whiteSpace(WhiteSpace.NoWrap)
                        ) {
                            // INJECTED: A Span to safely parse HTML like <sup> in dropdowns
                            Span(
                                attrs = Modifier.toAttrs {
                                    ref { htmlElement ->
                                        htmlElement.innerHTML = linkData.text
                                        onDispose {}
                                    }
                                })
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun HeaderLink(text: String, path: String, isActive: Boolean) {
    val palette = ColorMode.current.toSitePalette()
    Link(
        path = path,
        // REMOVED: text = text
        modifier = AnimatedHeaderLinkStyle.toModifier().fontSize(18.px)
            .fontWeight(if (isActive) FontWeight.Bold else FontWeight.Medium)
            .textDecorationLine(TextDecorationLine.None).whiteSpace(WhiteSpace.NoWrap).then(
                if (isActive) Modifier.backgroundColor(palette.brand.primarySoft).color(palette.brand.primary)
                else Modifier
            )
    ) {
        // INJECTED: A Span that safely parses the HTML inside the Link
        Span(
            attrs = Modifier.toAttrs {
                ref { htmlElement ->
                    htmlElement.innerHTML = text
                    onDispose {}
                }
            })
    }
}

@Composable
private fun MobileHeaderDropdown(
    title: String, items: List<NavLinkData>, isActive: Boolean, currentPath: String, onLinkClick: () -> Unit
) {
    var isExpanded by remember { mutableStateOf(isActive) }
    val palette = ColorMode.current.toSitePalette()

    Column(modifier = Modifier.fillMaxWidth()) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(topBottom = 12.px, leftRight = 16.px).borderRadius(8.px)
                .backgroundColor(if (isActive) palette.brand.primarySoft else Colors.Transparent).cursor(Cursor.Pointer)
                .onClick { isExpanded = !isExpanded },
            horizontalArrangement = Arrangement.SpaceBetween, // Pushes arrow to the far right
            verticalAlignment = Alignment.CenterVertically
        ) {
            Div(
                attrs = Modifier.color(if (isActive) palette.brand.primary else palette.custom.darkText)
                    .fontWeight(if (isActive) FontWeight.ExtraBold else FontWeight.Bold).fontSize(18.px).toAttrs {
                        ref { htmlElement ->
                            htmlElement.innerHTML = title
                            onDispose {}
                        }
                    })
            SpanText(
                text = if (isExpanded) "▲" else "▼",
                modifier = Modifier.color(if (isActive) palette.brand.primary else palette.custom.darkText)
                    .fontSize(14.px)
            )
        }

        if (isExpanded) {
            Column(
                modifier = Modifier.fillMaxWidth()
                    .padding(left = 16.px, bottom = 8.px, top = 4.px) // Indents sub-links so they feel nested
                    .borderLeft(2.px, LineStyle.Solid, palette.brand.primarySoft) // Subtle visual hierarchy line
            ) {
                items.forEach { linkData ->
                    key(linkData.text) {
                        val isSubActive = linkData.path.removeSuffix("/").ifEmpty { "/" } == currentPath
                        MobileHeaderLink(
                            text = linkData.text,
                            path = linkData.path,
                            isSubItem = true,
                            isActive = isSubActive,
                            onClick = onLinkClick
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun MobileHeaderLink(
    text: String, path: String, isSubItem: Boolean = false, isActive: Boolean = false, onClick: () -> Unit = {}
) {
    val palette = ColorMode.current.toSitePalette()
    Link(
        path = path,
        modifier = Modifier.fillMaxWidth().padding(topBottom = 12.px, leftRight = 16.px)
            .margin(left = if (isSubItem) 10.px else 0.px).borderRadius(8.px)
            .backgroundColor(if (isActive) palette.brand.primarySoft else Colors.Transparent)
            .color(if (isActive) palette.brand.primary else palette.custom.darkText)
            .fontWeight(if (isActive) FontWeight.ExtraBold else if (isSubItem) FontWeight.SemiBold else FontWeight.Bold)
            .fontSize(18.px).textDecorationLine(TextDecorationLine.None).onClick { onClick() }) {
        // INJECTED: A Span to safely parse the HTML (like <sup>) inside the Link
        Span(
            attrs = Modifier.toAttrs {
                ref { htmlElement ->
                    htmlElement.innerHTML = text
                    onDispose {}
                }
            },
        )
    }
}

// ==========================================
// DATA MODELS & CMS IMAGE SPECS
// ==========================================

data class NavLinkData(val text: String, val path: String)

/**
 * A Menu Item can either be a direct link (if path is provided)
 * OR a dropdown (if subItems list is populated).
 */
data class NavMenuItemData(
    val title: String, val path: String? = null, val subItems: List<NavLinkData> = emptyList()
)

/**
 * CMS IMAGE UPLOAD SPECS FOR HEADER:
 * @field logoPath:
 * - Aspect Ratio: Flexible (Horizontal/Landscape).
 * - Recommended Dimensions: Max height 80px, Max width 300px.
 * - Format: SVG (Ideal) or transparent PNG.
 * * @field utilityPhoneIcon / utilityEmailIcon:
 * - Aspect Ratio: 1:1 (Perfect Square).
 * - Recommended Dimensions: 24x24px.
 * - Format: SVG Only. Should be single-color (dark gray) to match text.
 */
data class NavHeaderData(
    val logoPath: String,
    val utilityPhoneIcon: String,
    val contactPhone: String,
    val utilityEmailIcon: String,
    val contactEmail: String,
    val utilityExtraLinkText: String,
    val utilityExtraLinkPath: String,
    val ctaText: String,
    val ctaPath: String,
    val menuItems: List<NavMenuItemData>
)

val defaultNavHeaderData = NavHeaderData(
    logoPath = Res.Images.Common.LOGO,
    utilityPhoneIcon = Res.Icons.CALL,
    contactPhone = activeStrings.supportPhone,
    utilityEmailIcon = Res.Icons.SEND_EMAIL,
    contactEmail = activeStrings.emailText,
    utilityExtraLinkText = "Find a Clinic",
    utilityExtraLinkPath = "/contact",
    ctaText = "Request Demo",
    ctaPath = "/contact",
    menuItems = listOf(
        NavMenuItemData(title = "MyHand<sup>®</sup> System", path = "/product"),
        NavMenuItemData(title = "Patients", path = "/patients"),
        NavMenuItemData(title = "Clinicians", path = "/clinicians"),
        // Converted Resources to a dropdown
        NavMenuItemData(
            title = "Resources", subItems = listOf(
                NavLinkData("Success Stories", "/testimonials"),
                NavLinkData("Resource Center", "/resources"),
//                NavLinkData("Find a Clinic", "/contact"),
                NavLinkData("Financing Options", "/financing")
            )
        ),
        NavMenuItemData(
            title = "Company", subItems = listOf(
                NavLinkData("About Us", "/about"),
                NavLinkData("Distributors", "/distributors"),
                NavLinkData("News & Press", "/news"),
                NavLinkData("Contact Us", "/contact")
            )
        )
    )
)
